import { createElement } from 'react';
import { createColumnHelper, type Row } from '@tanstack/react-table';
import { formatInstagramHandle } from '@/lib/channel-display';
import { unitLabel } from '@/lib/usage-unit';

export interface ChannelRow {
  id: string;
  type: 'whatsapp' | 'instagram' | 'facebook';
  displayName: string;
  metaWabaId: string;
  whatsappWabaName: string | null;
  whatsappDisplayPhoneNumber: string | null;
  instagramUsername: string | null;
  facebookPageName: string | null;
  tokenStatus: string;
  forwardingEnabled: boolean;
  metaConnected: boolean;
  /** Usage this quota period, in the OWNING ORG's meter (AIT-436) -- this list
   *  spans orgs, so `usageUnit` rides on the row rather than the page. */
  used: number;
  usageUnit: 'messages' | 'actions';
  workspace: { id: string; name: string };
  createdAt: string;
}

const col = createColumnHelper<ChannelRow>();

export const channelColumns = [
  col.accessor('type', {
    header: 'Type',
    enableSorting: true,
    meta: { headerClassName: 'w-[110px]', sortLabel: 'type' },
    cell: ({ row }) =>
      row.original.type === 'instagram' ? 'Instagram' : row.original.type === 'facebook' ? 'Facebook' : 'WhatsApp',
  }),
  col.accessor('displayName', {
    header: 'Name',
    enableSorting: true,
    meta: { cellClassName: 'font-medium', sortLabel: 'name' },
    cell: (c) => c.getValue(),
  }),
  col.accessor(
    (row) =>
      row.type === 'instagram'
        ? row.instagramUsername
          ? formatInstagramHandle(row.instagramUsername)
          : '—'
        : row.type === 'facebook'
          ? row.facebookPageName || '—'
          : row.whatsappDisplayPhoneNumber || '—',
    {
      id: 'contact',
      header: 'Phone / Handle',
      enableSorting: true,
      meta: {
        headerClassName: 'w-[160px]',
        cellClassName: 'font-mono text-xs',
        sortLabel: 'phone or handle',
      },
      cell: (c) => c.getValue(),
    },
  ),
  col.accessor('forwardingEnabled', {
    header: 'Messaging',
    enableSorting: true,
    meta: { headerClassName: 'w-[110px]', sortLabel: 'messaging' },
    cell: (c) => createElement(
      'span',
      {
        className: c.getValue()
          ? 'text-green-600'
          : 'text-muted-foreground',
      },
      c.getValue() ? 'Enabled' : 'Disabled',
    ),
  }),
  col.accessor('used', {
    header: 'Usage (mo)',
    enableSorting: true,
    meta: {
      headerClassName: 'w-[130px]',
      cellClassName: 'tabular-nums',
      sortLabel: 'usage',
    },
    cell: (c) => `${c.getValue().toLocaleString()} ${unitLabel(c.row.original.usageUnit)}`,
  }),
  col.accessor((row) => row.workspace.name, {
    id: 'workspace',
    header: 'Workspace',
    enableSorting: true,
    meta: { cellClassName: 'text-muted-foreground', sortLabel: 'workspace' },
    cell: (c) => c.getValue(),
  }),
];

export function channelGlobalFilter(
  row: Row<ChannelRow>,
  _columnId: string,
  value: string,
): boolean {
  const q = value.toLowerCase();
  return [
    row.original.id,
    row.original.displayName,
    row.original.whatsappWabaName,
    row.original.whatsappDisplayPhoneNumber,
    row.original.instagramUsername,
    row.original.facebookPageName,
    row.original.metaWabaId,
    row.original.workspace.name,
  ]
    .filter(Boolean)
    .some((v) => v!.toLowerCase().includes(q));
}
